import React, { useRef, useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import { useWizard } from "@contexts/WizardContext";
import { BoardProvider } from "@contexts/BoardContext";
import { useConfig } from "@contexts/ConfigContext";
import { useThemeMode } from "@contexts/ThemeContext";
import { useTranslation } from "react-i18next";
import { useBoardCreationWizardViewModel } from "./BoardCreationWizard.ViewModel";
import { WizardStepper } from "./WizardStepper";
import { ConnectToolsStep } from "./steps/ConnectToolsStep";
import { WorkflowSetupStep } from "./steps/WorkflowSetupStep";
import type { WorkflowSetupStepHandle } from "./steps/WorkflowSetupStep";
import UpgradePrompt from "@features/billing/UpgradePrompt";
import "./onboarding-wizard.scss";

const BoardCreationWizardOverlay: React.FC = () => {
  const { t } = useTranslation();
  const { isWizardOpen } = useWizard();
  const vm = useBoardCreationWizardViewModel();
  const stepRef = useRef<WorkflowSetupStepHandle>(null);
  const [isStepSaving, setIsStepSaving] = useState(false);
  const { config } = useConfig();
  const { themeMode } = useThemeMode();
  const iconBase = config?.regionBaseUrl ?? "";
  const logoSrc = `${iconBase}/assets/img/logo_full_horizontal${themeMode === "dark" ? "_inverted" : ""}_32.png`;

  return (
    <>
      <UpgradePrompt
        refusal={vm.billingRefusal}
        onDismiss={vm.dismissBillingRefusal}
      />
      <Dialog
        open={isWizardOpen && !vm.billingRefusal}
        onOpenChange={(_, data) => {
          if (!data.open) void vm.saveAndExit();
        }}
      >
        <DialogSurface className="onboarding-wizard__surface">
          <DialogBody>
            <DialogTitle className="onboarding-wizard__title">
              <div className="onboarding-wizard__title-row">
                <span>{vm.titleText}</span>
                <img
                  src={logoSrc}
                  height={28}
                  alt={t("app.name", "OneTaskList")}
                  className="onboarding-wizard__logo"
                />
              </div>
              <WizardStepper
                currentStep={vm.currentStep}
                step1Label={vm.step1LabelText}
                step2Label={vm.step2LabelText}
                step3Label={vm.step3LabelText}
              />
            </DialogTitle>

            <DialogContent>
              {vm.isCreatingBoard && (
                <div className="onboarding-wizard__loading-body">
                  <Spinner />
                </div>
              )}

              {!vm.isCreatingBoard && vm.wizardBoardId && (
                <BoardProvider boardId={vm.wizardBoardId}>
                  {vm.currentStep === 1 && (
                    <ConnectToolsStep onContinue={() => vm.goToStep(2)} />
                  )}
                  {vm.currentStep === 2 && (
                    <WorkflowSetupStep
                      ref={stepRef}
                      wizardBoardId={vm.wizardBoardId}
                      onFinish={vm.finishWizard}
                      onIsSavingChange={setIsStepSaving}
                    />
                  )}
                </BoardProvider>
              )}
            </DialogContent>

            <DialogActions fluid>
              {vm.currentStep === 1 && !vm.isCreatingBoard && (
                <Button appearance="primary" onClick={() => vm.goToStep(2)}>
                  {vm.continueText}
                </Button>
              )}
              {vm.currentStep === 2 && (
                <>
                  <Button appearance="secondary" onClick={() => vm.goToStep(1)}>
                    {vm.backText}
                  </Button>
                  <Button
                    appearance="primary"
                    disabled={isStepSaving}
                    onClick={() => void stepRef.current?.finish()}
                  >
                    {isStepSaving ? <Spinner size="tiny" /> : vm.finishText}
                  </Button>
                </>
              )}
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};

export default BoardCreationWizardOverlay;
